// frontend/src/services/api.js

// IMPORTANT:
// Base URL is now hard-coded to production.
// Localhost has been removed.
const API_BASE_URL = "https://booking.isttestdev.ng/api";

console.log("API Base URL:", API_BASE_URL);

class APIService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem("access_token");

    const config = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    };

    if (token && !options.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);

      if (response.status === 401 && !options.skipAuth) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          config.headers.Authorization = `Bearer ${localStorage.getItem(
            "access_token"
          )}`;
          const retryResponse = await fetch(url, config);
          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`);
          }
          return await retryResponse.json();
        } else {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          window.location.href = "/login";
          return;
        }
      }

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData?.detail) {
            errorMessage =
              typeof errorData.detail === "string"
                ? errorData.detail
                : JSON.stringify(errorData.detail);
          }
        } catch (_) {}
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return await response.json();
      }

      return response;
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) return false;

      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  get(endpoint, params = {}, options = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, {
      method: "GET",
      ...options,
    });
  }

  post(endpoint, data = {}, options = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
      ...options,
    });
  }

  put(endpoint, data = {}, options = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
      ...options,
    });
  }

  delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: "DELETE",
      ...options,
    });
  }
}

export const apiService = new APIService();

// -------------------- AUTH API --------------------
export const authAPI = {
  register(userData) {
    return apiService.post("/auth/register", userData, { skipAuth: true });
  },

  login(email, password) {
    return apiService.post(
      "/auth/login",
      { email, password },
      { skipAuth: true }
    );
  },

  refreshToken(refreshToken) {
    return apiService.post(
      "/auth/refresh",
      { refresh_token: refreshToken },
      { skipAuth: true }
    );
  },

  getProfile() {
    return apiService.get("/users/profile");
  },

  updateProfile(userData) {
    return apiService.put("/users/profile", userData);
  },

  deleteAccount() {
    return apiService.delete("/users/profile");
  },
};

// -------------------- FLIGHTS API --------------------
export const flightsAPI = {
  searchFlights(searchParams) {
    const params = {
      origin: searchParams.origin,
      destination: searchParams.destination,
      departure_date: searchParams.departureDate,
      passengers: searchParams.passengers,
      class_type: searchParams.classType,
    };

    if (searchParams.returnDate) {
      params.return_date = searchParams.returnDate;
    }

    return apiService.get("/flights/search", params);
  },

  getFlightDetails(flightId) {
    return apiService.get(`/flights/${flightId}`);
  },

  getAirlines() {
    return apiService.get("/flights/airlines");
  },

  getAirports() {
    return apiService.get("/flights/airports");
  },
};

// -------------------- BOOKINGS API --------------------
export const bookingsAPI = {
  createBooking(bookingData) {
    const isLoggedIn = !!localStorage.getItem("access_token");
    const options = isLoggedIn ? {} : { skipAuth: true };
    return apiService.post("/bookings/", bookingData, options);
  },

  confirmPayment(bookingId, paymentConfirmation) {
    return apiService.post(
      `/bookings/${bookingId}/confirm-payment`,
      paymentConfirmation,
      { skipAuth: true }
    );
  },

  getUserBookings() {
    return apiService.get("/bookings/");
  },

  getBookingDetails(bookingId) {
    return apiService.get(`/bookings/${bookingId}`, {}, { skipAuth: true });
  },

  getBookingByReference(bookingReference) {
    return apiService.get(
      `/bookings/reference/${bookingReference}`,
      {},
      { skipAuth: true }
    );
  },

  processPayment(bookingId, paymentData) {
    return apiService.post(`/bookings/${bookingId}/pay`, paymentData);
  },

  cancelBooking(bookingId) {
    return apiService.delete(`/bookings/${bookingId}`);
  },
};
